/* eslint-disable @typescript-eslint/no-explicit-any */
import { CalendarIcon } from "@src/assets/icons/CalendarIcon";
import { CSSProperties } from "react";
import React, { useCallback, useMemo } from "react";
import ReactDatePicker from "react-datepicker";
import { Control, useController } from "react-hook-form";
import { CalendarIconContainer, DatePickerWrapper, MainContainer } from "./DatePicker.styled";
import "react-datepicker/dist/react-datepicker.css";
import { enUS } from "date-fns/locale";

type TDateOrNull = Date | null;
type TDateRange = [Date | null, Date | null];

interface DatePickerProps {
	control: Control<any>;
	name: string;
	variant?: "single" | "range";
	controlSize?: "n" | "sm";
	wrapperStyle?: CSSProperties;
	placeholder?: string;
	minDate?: Date;
	maxDate?: Date;
	className?: string;
	wrapperClassName?: string;
	disabled?: boolean;
	highlightDates?: Array<Date>;
}

const DatePickerComponent = ({
	control,
	name,
	variant = "single",
	controlSize = "n",
	wrapperStyle,
	placeholder = "Default placeholder",
	minDate,
	maxDate,
	className,
	wrapperClassName,
	disabled,
	highlightDates,
	...rest
}: DatePickerProps) => {
	const { field, fieldState } = useController({ name, control });

	const formatDate = useCallback((date: Date | null): string => {
		if (!date) return "";
		const year = date.getFullYear();
		const month = (1 + date.getMonth()).toString().padStart(2, "0");
		const day = date.getDate().toString().padStart(2, "0");
		return `${month}/${day}/${year}`;
	}, []);

	const handleChange = useCallback(
		(data: TDateOrNull | TDateRange) => {
			if (variant === "range") {
				const [start, end] = data as TDateRange;
				const startStr = formatDate(start);
				const endStr = formatDate(end);
				field.onChange(`${startStr} - ${endStr}`);
			} else {
				const date = data as TDateOrNull;
				field.onChange(formatDate(date));
			}
		},
		[variant, formatDate, field],
	);

	const selected: TDateOrNull = useMemo(() => {
		return variant === "single" && field.value ? new Date(field.value as string) : null;
	}, [variant, field.value]);

	const selectedRange: TDateRange = useMemo(() => {
		if (variant === "range" && typeof field.value === "string") {
			const parts = (field.value as string).split(" - ");
			return [parts[0] ? new Date(parts[0]) : null, parts[1] ? new Date(parts[1]) : null];
		}
		return [null, null];
	}, [variant, field.value]);

	const { invalid } = fieldState;

	return (
		<MainContainer style={wrapperStyle} $controlSize={controlSize} {...rest}>
			<DatePickerWrapper $isError={invalid}>
				<ReactDatePicker
					placeholderText={placeholder}
					{...(variant === "range"
						? {
							selectsRange: true,
							startDate: selectedRange[0] ?? undefined,
							endDate: selectedRange[1] ?? undefined,
						}
						: { selected: selected ?? undefined })}
					minDate={minDate}
					maxDate={maxDate}
					onChange={handleChange}
					dateFormat="MM/dd/yyyy"
					dateFormatCalendar="MMMM"
					showYearDropdown
					dropdownMode="select"
					scrollableYearDropdown
					todayButton="Today"
					locale={enUS}
					wrapperClassName={wrapperClassName}
					className={className}
					disabled={disabled}
					highlightDates={highlightDates}
				/>
			</DatePickerWrapper>
			<CalendarIconContainer>
				<CalendarIcon />
			</CalendarIconContainer>
		</MainContainer>
	);
};

export const DatePicker = React.memo(DatePickerComponent);
